/**
 * RoamerSystem — roam mode's visible wild Pokémon (diegetic ladder
 * rung 1: presence). Phaser wrapper over the pure helpers in
 * roamerLogic.ts, same split as follower.ts / followerState.ts.
 *
 * Renders per-region casts of wandering wild sprites from the same
 * `follower-ow:<species>` sheets the follower uses (32×32, 9-frame
 * TaTaTaZJJ layout — OW_IDLE_FRAME / OW_WALK_FRAMES / flipX-for-right,
 * imported from follower.ts). Origin (0.5, 1) + depth 4, matching the
 * follower's render contract. Steps amble at 280 ms/tile — half the
 * player's 140 ms cadence, a deliberate "grazing, not patrolling" read.
 *
 * v1 contracts (locked in the 2026-08-02 design exploration + its
 * adversarial review — see project memory / TODOS):
 *   - PLAYER-INITIATED CONTACT ONLY. Roamers never step onto the
 *     player's or follower's tile, so there are no roamer-initiated
 *     battle launches and no two-roamer arbitration races. Engagement
 *     is a bump (blocked step), an adjacent interact, or a COMMAND
 *     tap routed through the interactable-approach flow.
 *   - NOT in npcTiles. Trainer-LOS cones don't break on roamers, and
 *     canStep ignores them; the WorldScene engagement intercept + the
 *     pathfinder's avoid-tiles consult tileList() explicitly. (A body
 *     randomly shielding you from a Forest trainer's cone would be a
 *     gameplay rule nobody chose — revisit deliberately if wanted.)
 *   - Per-region groups keyed by runState.spawnVisits. A group with
 *     live roamers survives border hops (continuity); a fully
 *     consumed group refills only on genuine re-entry — the respawn
 *     policy that keeps the train-up loop's grinding opt-in without
 *     starving it.
 *   - Positions derived, never persisted (the follower's rule). A
 *     boot-resume re-derives the SAME cast from (master, region,
 *     visit) — wander phase resets, species/levels/spawn tiles never
 *     re-roll. If a derived spawn tile is momentarily blocked at
 *     spawn time (NPC/player standing there), the spawn nudges to the
 *     first free patch tile in ascending index order — deterministic
 *     given the same world state, rng-free so later draws never shift.
 *
 * Ticking is driven by WorldScene.update with the freeze gates at the
 * call site (dialogue, transitions, pending encounters); scene SLEEP
 * during battles/modals stops update entirely, so battles freeze the
 * grass for free.
 */
import Phaser from 'phaser';
import { seedStore } from '../state/seed';
import {
  OW_IDLE_FRAME,
  OW_WALK_FRAMES,
  encodedDirection,
} from './follower';
import type { Facing } from './followerState';
import {
  derivePopulation,
  findGrassPatches,
  pickWanderStep,
  type GrassPatch,
} from './roamerLogic';

/** Amble cadence — 2× the player's STEP_COOLDOWN_MS. */
const ROAM_STEP_MS = 280;
/** Idle dwell between steps, uniformly drawn from [min, max). */
const IDLE_MIN_MS = 900;
const IDLE_MAX_MS = 2800;
const ROAMER_DEPTH = 4;

export interface Roamer {
  id: string;
  region: string;
  species: string;
  level: number;
  /** Ordinal in the visit's derived cast — the persistence key for
   *  consumption (runState.spawnConsumed), so a reload re-derives the
   *  cast and then removes exactly the roamers already battled. */
  spawnIndex: number;
  /** Committed logical tile (global coords). Advances at step START —
   *  the player's own stepTo convention — so occupancy checks see the
   *  destination as taken for the whole lerp. */
  x: number;
  y: number;
  patchTiles: ReadonlySet<number>;
  facing: Facing;
  sprite: Phaser.GameObjects.Sprite;
  stepping: boolean;
  fromX: number;
  fromY: number;
  stepElapsedMs: number;
  idleRemainingMs: number;
  stepCounter: number;
}

interface RegionGroup {
  visit: number;
  roamers: Roamer[];
  /** Wander rng — the derived spawn stream, continued past the
   *  population draws. Non-persisted by design: wander phase resets on
   *  reload, the cast doesn't. */
  rng: () => number;
}

export interface RoamerHost {
  /** Full-grid stride (mega-map worldWidth in exterior mode). */
  gridWidth(): number;
  tileSize(): number;
  /** True if the behavior byte at (x, y) is tall grass. */
  isGrass(x: number, y: number): boolean;
  /** True if a roamer must not stand on (x, y): terrain/NPC collision
   *  via canStep, plus the player's and follower's tiles. */
  isBlockedForRoamer(x: number, y: number): boolean;
}

export class RoamerSystem {
  private scene: Phaser.Scene;
  private host: RoamerHost;
  private groups = new Map<string, RegionGroup>();
  private nextId = 0;

  constructor(scene: Phaser.Scene, host: RoamerHost) {
    this.scene = scene;
    this.host = host;
  }

  /** True if the region already has a group with un-consumed roamers —
   *  the caller skips populate so border hops keep continuity. */
  hasLiveGroup(region: string): boolean {
    const g = this.groups.get(region);
    return Boolean(g && g.roamers.length > 0);
  }

  /**
   * Build the region's cast for `visit`. Caller owns the gates (roam
   * mode, starter, exterior, table exists, visit bookkeeping) — this
   * just derives + mounts. Replaces any existing group for the region.
   * `consumed` holds spawn ordinals already battled this visit
   * (runState.spawnConsumed): the FULL cast is always derived (fixed
   * rng draw shape), then consumed ordinals are dropped — so a reload
   * restores the patch exactly as the player left it.
   */
  populate(
    region: string,
    aabb: { x: number; y: number; w: number; h: number },
    visit: number,
    consumed: ReadonlySet<number>,
  ): void {
    this.disposeGroup(region);
    const width = this.host.gridWidth();
    const patches: GrassPatch[] = findGrassPatches(
      (x, y) => this.host.isGrass(x, y),
      aabb,
      width,
    );
    const rng = seedStore.spawnRngFor(region, visit);
    const spawns = derivePopulation(region, patches, rng, width);
    const patchSets = new Map<number, Set<number>>();
    for (const p of patches) patchSets.set(p.id, new Set(p.tiles));

    const roamers: Roamer[] = [];
    const taken = new Set<number>();
    for (const [spawnIndex, spawn] of spawns.entries()) {
      if (consumed.has(spawnIndex)) continue;
      const patchTiles = patchSets.get(spawn.patchId)!;
      let { x, y } = spawn;
      // Deterministic rng-free nudge if the derived tile is blocked
      // right now (player/NPC standing there) or already taken by an
      // earlier spawn this pass.
      if (taken.has(y * width + x) || this.host.isBlockedForRoamer(x, y)) {
        const free = [...patchTiles]
          .sort((a, b) => a - b)
          .find(
            (t) =>
              !taken.has(t) &&
              !this.host.isBlockedForRoamer(t % width, Math.floor(t / width)),
          );
        if (free === undefined) continue; // patch fully occupied — skip
        x = free % width;
        y = Math.floor(free / width);
      }
      taken.add(y * width + x);

      const t = this.host.tileSize();
      const sprite = this.scene.add
        .sprite(
          (x + 0.5) * t,
          (y + 1) * t,
          `follower-ow:${spawn.species}`,
          OW_IDLE_FRAME.down,
        )
        .setOrigin(0.5, 1)
        .setDepth(ROAMER_DEPTH);
      roamers.push({
        id: `roamer-${this.nextId++}`,
        region,
        species: spawn.species,
        level: spawn.level,
        spawnIndex,
        x,
        y,
        patchTiles,
        facing: 'down',
        sprite,
        stepping: false,
        fromX: x,
        fromY: y,
        stepElapsedMs: 0,
        idleRemainingMs: IDLE_MIN_MS + rng() * (IDLE_MAX_MS - IDLE_MIN_MS),
        stepCounter: 0,
      });
    }
    this.groups.set(region, { visit, roamers, rng });
  }

  /** All live roamer sprites — for the UI camera ignore list. */
  getSprites(): Phaser.GameObjects.Sprite[] {
    const out: Phaser.GameObjects.Sprite[] = [];
    for (const g of this.groups.values()) {
      for (const r of g.roamers) out.push(r.sprite);
    }
    return out;
  }

  /** Committed roamer tiles — COMMAND-mode avoid points + occupancy. */
  tileList(): Array<{ x: number; y: number }> {
    const out: Array<{ x: number; y: number }> = [];
    for (const g of this.groups.values()) {
      for (const r of g.roamers) out.push({ x: r.x, y: r.y });
    }
    return out;
  }

  /** The roamer whose committed tile is (x, y), if any. */
  roamerAt(x: number, y: number): Roamer | null {
    for (const g of this.groups.values()) {
      for (const r of g.roamers) {
        if (r.x === x && r.y === y) return r;
      }
    }
    return null;
  }

  /** The roamer physically blocking (x, y): committed tile OR the
   *  source tile it's still visually vacating mid-step. Player-step
   *  intercepts + path-walkability use this so a 280ms lerp window
   *  can't produce battles on visually-empty grass or sprite overlap
   *  on a visually-occupied tile. */
  roamerBlockingAt(x: number, y: number): Roamer | null {
    for (const g of this.groups.values()) {
      for (const r of g.roamers) {
        if (r.x === x && r.y === y) return r;
        if (r.stepping && r.fromX === x && r.fromY === y) return r;
      }
    }
    return null;
  }

  /**
   * Startle-hop the engaged wild one tile along `dir` (away from the
   * bumping player) so the duel has a gap to stage in — adjacent bumps
   * otherwise drop the battler onto the player's own tile (the
   * findDuelSlot fallback). Updates the committed tile; the 160ms hop
   * runs under the duel input regime so nothing else moves. Stays
   * inside the home patch (the escape guarantee) and no-ops if the
   * back tile is blocked — the caller's fallback staging still works.
   */
  /** Whether `roamer` could startle-hop onto (x, y): inside its own
   *  grass patch, unblocked, and not another roamer's tile — with
   *  roamerBlockingAt semantics (committed AND mid-step source tiles;
   *  a neighbor frozen mid-lerp by the engagement gate straddles both
   *  for the whole duel), excluding the hopping roamer itself. The
   *  single source of hop validity — planWildStaging predicts with it
   *  and startleBack executes with it, so plan and execution can't
   *  drift. */
  canStartleTo(
    roamer: Roamer,
    x: number,
    y: number,
    isBlocked: (x: number, y: number) => boolean,
  ): boolean {
    const width = this.host.gridWidth();
    if (!roamer.patchTiles.has(y * width + x)) return false;
    if (isBlocked(x, y)) return false;
    const occupant = this.roamerBlockingAt(x, y);
    if (occupant && occupant !== roamer) return false;
    return true;
  }

  /** Cornered startle (rungs 2–4 of the Cornered ladder): the wild
   *  whirls to face `towardTile` and does a quick hop IN PLACE —
   *  nowhere to flee, so the startle beat plays without a tile move.
   *  Gives the player's own give-ground step a visible cause; without
   *  it, un-commanded avatar movement reads as rubber-banding. */
  async startleInPlace(
    roamer: Roamer,
    towardTile: { x: number; y: number },
  ): Promise<void> {
    this.faceToward(roamer, towardTile);
    const t = this.host.tileSize();
    const homeX = (roamer.x + 0.5) * t;
    const homeY = (roamer.y + 1) * t;
    // Settle a mid-lerp sprite onto its committed tile first: the
    // engagement freeze (encounterPending gates update()) can catch a
    // wild up to a full tile off-grid, and the whole duel — midpoint,
    // HP float, lunge targets — is anchored to the committed tile.
    if (
      Math.abs(roamer.sprite.x - homeX) > 1 ||
      Math.abs(roamer.sprite.y - homeY) > 1
    ) {
      roamer.stepping = false;
      await new Promise<void>((resolve) => {
        this.scene.tweens.add({
          targets: roamer.sprite,
          x: homeX,
          y: homeY,
          duration: 120,
          ease: 'Quad.easeOut',
          onComplete: () => resolve(),
        });
      });
    }
    await new Promise<void>((resolve) => {
      this.scene.tweens.add({
        targets: roamer.sprite,
        y: homeY - 6,
        duration: 80,
        yoyo: true,
        ease: 'Quad.easeOut',
        onComplete: () => resolve(),
      });
    });
    roamer.sprite.setY(homeY);
  }

  async startleBack(
    roamer: Roamer,
    dir: { x: number; y: number },
    isBlocked: (x: number, y: number) => boolean,
  ): Promise<boolean> {
    const bx = roamer.x + dir.x;
    const by = roamer.y + dir.y;
    if (!this.canStartleTo(roamer, bx, by, isBlocked)) return false;
    roamer.fromX = roamer.x;
    roamer.fromY = roamer.y;
    roamer.x = bx;
    roamer.y = by;
    roamer.stepping = false; // driven by this tween, not the wander tick
    const t = this.host.tileSize();
    // Face the direction it was startled FROM (toward the player).
    const facing: Facing =
      dir.x > 0 ? 'left' : dir.x < 0 ? 'right' : dir.y > 0 ? 'up' : 'down';
    roamer.facing = facing;
    roamer.sprite.setFlipX(facing === 'right');
    roamer.sprite.setFrame(OW_IDLE_FRAME[encodedDirection(facing)]);
    await new Promise<void>((resolve) => {
      this.scene.tweens.add({
        targets: roamer.sprite,
        x: (bx + 0.5) * t,
        y: (by + 1) * t,
        duration: 160,
        ease: 'Quad.easeOut',
        onComplete: () => resolve(),
      });
    });
    return true;
  }

  /** Face a roamer toward `tile`. Engagement calls this
   *  unconditionally — a wild snuck up on from behind must whirl
   *  around to meet its challenger, not fight backwards (first phone
   *  dogfood, 2026-08-03). */
  faceToward(roamer: Roamer, tile: { x: number; y: number }): void {
    const dx = tile.x - roamer.x;
    const dy = tile.y - roamer.y;
    const facing: Facing =
      Math.abs(dx) >= Math.abs(dy)
        ? dx >= 0
          ? 'right'
          : 'left'
        : dy > 0
          ? 'down'
          : 'up';
    roamer.facing = facing;
    roamer.sprite.setFlipX(facing === 'right');
    roamer.sprite.setFrame(OW_IDLE_FRAME[encodedDirection(facing)]);
  }

  /** Remove an engaged roamer (contact → battle). The group stays —
   *  an emptied group refills only on genuine region re-entry. */
  consume(roamer: Roamer): void {
    const g = this.groups.get(roamer.region);
    if (!g) return;
    const i = g.roamers.indexOf(roamer);
    if (i >= 0) g.roamers.splice(i, 1);
    roamer.sprite.destroy();
  }

  /** Tear down every group + sprite (interior load, scene shutdown). */
  clearAll(): void {
    for (const region of [...this.groups.keys()]) this.disposeGroup(region);
  }

  private disposeGroup(region: string): void {
    const g = this.groups.get(region);
    if (!g) return;
    for (const r of g.roamers) r.sprite.destroy();
    this.groups.delete(region);
  }

  /** Advance wander state. Caller gates on dialogue / transition /
   *  encounterPending; scene SLEEP stops update() entirely. */
  update(delta: number): void {
    const width = this.host.gridWidth();
    const t = this.host.tileSize();
    for (const g of this.groups.values()) {
      for (const r of g.roamers) {
        if (r.stepping) {
          r.stepElapsedMs += delta;
          const p = Math.min(1, r.stepElapsedMs / ROAM_STEP_MS);
          const x = r.fromX + (r.x - r.fromX) * p;
          const y = r.fromY + (r.y - r.fromY) * p;
          r.sprite.setPosition((x + 0.5) * t, (y + 1) * t);
          if (p >= 1) {
            r.stepping = false;
            r.sprite.setFrame(OW_IDLE_FRAME[encodedDirection(r.facing)]);
            r.idleRemainingMs =
              IDLE_MIN_MS + g.rng() * (IDLE_MAX_MS - IDLE_MIN_MS);
          }
          continue;
        }
        r.idleRemainingMs -= delta;
        if (r.idleRemainingMs > 0) continue;
        const step = pickWanderStep(
          g.rng,
          { x: r.x, y: r.y },
          r.patchTiles,
          (x, y) =>
            this.host.isBlockedForRoamer(x, y) ||
            this.isRoamerTile(x, y),
          width,
        );
        if (!step) {
          // Boxed in — idle another beat.
          r.idleRemainingMs = IDLE_MIN_MS + g.rng() * (IDLE_MAX_MS - IDLE_MIN_MS);
          continue;
        }
        r.fromX = r.x;
        r.fromY = r.y;
        r.x = step.x;
        r.y = step.y;
        r.facing = step.dir;
        r.stepping = true;
        r.stepElapsedMs = 0;
        r.stepCounter++;
        const pair = OW_WALK_FRAMES[encodedDirection(r.facing)];
        r.sprite.setFlipX(r.facing === 'right');
        r.sprite.setFrame(r.stepCounter % 2 === 0 ? pair[0] : pair[1]);
      }
    }
  }

  private isRoamerTile(x: number, y: number): boolean {
    return this.roamerAt(x, y) !== null;
  }
}
